"use strict";
/**
 * Tap pad rendering (ai/TAP_PAD_PLAN.md). Pure: markup in, markup out.
 *
 * Laid out for a quick glance on a phone in portrait: SNAP and DONE side by
 * side on top, the rhythm of every play; yardage next, since a big gain is
 * entered while the play is still running; then the result groups in the order
 * a play resolves; UNDO apart at the bottom so it is not hit by accident.
 * Buttons fire on pointerdown (handlers/tap-pad-handler.ts), so nothing here
 * may reflow when a tap lands.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.yardsLabel = exports.TAP_LABELS = void 0;
exports.tapLabel = tapLabel;
exports.renderTapPad = renderTapPad;
exports.renderTapStatus = renderTapStatus;
exports.renderTapHistory = renderTapHistory;
exports.renderPlayState = renderPlayState;
const tap_vocabulary_1 = require("../../shared/tap-vocabulary");
const tap_parked_1 = require("./tap-parked");
exports.TAP_LABELS = {
    snap: "SNAP",
    done: "DONE",
    yards: "yards",
    touchdown: "TD",
    field_goal: "FG good",
    safety: "Safety",
    go_two: "Going for 2",
    xp_good: "XP good",
    xp_miss: "XP miss",
    two_good: "2PT good",
    two_fail: "2PT fail",
    turnover: "Turnover",
    punt: "Punt",
    fg_missed: "FG miss",
    downs: "Downs",
    first_down: "1st down",
    gain_short: "Gain",
    no_gain: "No gain",
    incomplete: "Incomplete",
    sack: "Sack",
    flag: "FLAG",
    pull: "PULL QUOTES",
    undo: "UNDO",
};
function escapeHtml(value) {
    return value.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
}
function button(kind, cls, label = exports.TAP_LABELS[kind], side = null, value = null) {
    const sideAttr = side ? ` data-side="${side}"` : "";
    const valueAttr = value === null ? "" : ` data-value="${value}"`;
    return `<button type="button" class="tap-btn ${cls}" data-tap-kind="${kind}"${sideAttr}${valueAttr}>${escapeHtml(label)}</button>`;
}
/** "+40", "−5". */
const yardsLabel = (v) => (v < 0 ? `−${Math.abs(v)}` : `+${v}`);
exports.yardsLabel = yardsLabel;
/** What one tap says, in the words on its button. */
function tapLabel(t, teams) {
    if (t.kind === "touchdown" && t.side)
        return `TD ${t.side === "home" ? teams.home : teams.away}`;
    if (t.kind === "yards" && t.value !== null)
        return (0, exports.yardsLabel)(t.value);
    return exports.TAP_LABELS[t.kind];
}
function renderTapPad(teams, title) {
    return `<div class="tap-pad">
		<div class="tap-pad-head">
			<button type="button" class="tap-pad-exit btn btn-small">Game</button>
			<span class="tap-pad-title">${escapeHtml(title)}</span>
			${button("pull", "tap-pull")}
		</div>
		<div class="tap-pad-status" role="status">${tap_parked_1.PARKED_HTML}<span class="tap-status-text"></span></div>
		<div class="tap-play-state" role="status"></div>
		<div class="tap-frame">
			${button("snap", "tap-snap")}
			${button("done", "tap-done")}
		</div>
		<div class="tap-group tap-yards">
			${tap_vocabulary_1.YARD_BUCKETS.map((v) => button("yards", "", (0, exports.yardsLabel)(v), null, v)).join("")}
		</div>
		<div class="tap-group tap-score">
			${button("touchdown", "tap-td", `TD ${teams.away}`, "away")}
			${button("touchdown", "tap-td", `TD ${teams.home}`, "home")}
			${button("field_goal", "")}
			${button("safety", "")}
		</div>
		<div class="tap-group tap-try">
			${button("go_two", "tap-go-two")}
			${button("xp_good", "")}
			${button("xp_miss", "")}
			${button("two_good", "")}
			${button("two_fail", "")}
		</div>
		<div class="tap-group tap-change">
			${button("turnover", "")}
			${button("punt", "")}
			${button("fg_missed", "")}
			${button("downs", "")}
		</div>
		<div class="tap-group tap-play">
			${button("first_down", "")}
			${button("gain_short", "")}
			${button("no_gain", "")}
			${button("incomplete", "")}
			${button("sack", "")}
			${button("flag", "tap-flag")}
		</div>
		${button("undo", "tap-undo")}
		<ol class="tap-history" aria-label="Last taps"></ol>
	</div>`;
}
function renderTapStatus(s) {
    const parts = [s.pending === 0 ? "all sent" : `${s.pending} unsent`];
    if (s.offsetMs === null)
        parts.push("clock not synced");
    else
        parts.push(`clock ${s.offsetMs >= 0 ? "+" : ""}${Math.round(s.offsetMs)} ms (rtt ${Math.round(s.rttMs ?? 0)}, ${s.syncedAgoS ?? 0}s ago)`);
    if (s.conflicts)
        parts.push(`${s.conflicts} refused`);
    if (s.error)
        parts.push(`retrying: ${s.error}`);
    return escapeHtml(parts.join(" · "));
}
function renderTapHistory(items, undone, teams, limit = 8) {
    return items
        .slice(-limit)
        .reverse()
        .map((t) => {
        const at = new Date(t.atMs).toLocaleTimeString(undefined, { hour12: false });
        const play = t.playSeq === null ? "" : ` #${t.playSeq}`;
        return `<li class="${undone.has(t.tapId) ? "tap-undone" : ""}">${escapeHtml(at)}${play} ${escapeHtml(tapLabel(t, teams))}</li>`;
    })
        .join("");
}
/** The open play and what DONE would close it with, or that none is open. */
function renderPlayState(state, teams) {
    if (!state.open)
        return escapeHtml(state.lastSeq ? `between plays · #${state.lastSeq} done` : "between plays");
    const said = state.open.taps.filter((t) => t.kind !== "undo" && !state.undone.has(t.tapId)).map((t) => tapLabel(t, teams));
    return escapeHtml([`PLAY #${state.open.seq} OPEN`, ...said].join(" · "));
}
